import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  InputAdornment, IconButton, Chip
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { getAccent, getBg, getTextColor } from '../../theme/themes';
import UserAvatar from '../shared/UserAvatar';
import api from '../../services/api';

const PINK = {
  main: '#e91e8c',
  light: '#f06292',
  dark: '#c2185b',
  hot: '#ff1493',
  bg: '#fce4ec',
  darkBg: '#2d1a24',
  gradient: 'linear-gradient(135deg, #e91e8c 0%, #f06292 50%, #ff1493 100%)',
  glow: '0 0 24px #e91e8c44, 0 8px 32px #e91e8c22',
  border: '#e91e8c33',
};

const PinDot = ({ filled, isDark }) => (
  <motion.div
    animate={{ scale: filled ? [1, 1.3, 1] : 1 }}
    transition={{ duration: 0.2 }}
  >
    <Box
      sx={{
        width: 16, height: 16, borderRadius: '50%',
        bgcolor: filled ? PINK.main : 'transparent',
        border: `2.5px solid ${filled ? PINK.main : isDark ? '#555' : '#ccc'}`,
        transition: 'background 0.15s',
        boxShadow: filled ? `0 0 8px ${PINK.main}66` : 'none',
      }}
    />
  </motion.div>
);

const RoleSelectPage = ({ demoHints, onFirstLogin }) => {
  const { isDark } = useThemeMode();
  const { login, refreshUser, demoModeEnabled } = useAuth();

  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    api.get('/kiosk/children')
      .then(r => setChildren(r.data))
      .catch(() => {})
      .finally(() => setLoadingChildren(false));
  }, []);

  useEffect(() => {
    if (demoHints?.parentCredentials) {
      const [u, p] = demoHints.parentCredentials.split(' / ');
      if (u) setUsername(u);
      if (p) setPassword(p);
    }
  }, [demoHints]);

  const handleChildClick = (child) => {
    setSelectedChild(child);
    setPin('');
    setPinError('');

    if (demoModeEnabled) {
      const digits = '0000';
      digits.split('').forEach((d, i) => {
        setTimeout(() => {
          setPin(prev => prev + d);
        }, 300 + i * 250);
      });
      setTimeout(() => {
        handlePinSubmit(child, '0000');
      }, 300 + 4 * 250 + 200);
    }
  };

  const handlePinSubmit = async (child, fullPin) => {
    setPinSubmitting(true);
    try {
      const res = await api.post('/auth/pin-login', { userId: child.id, pin: fullPin });
      localStorage.setItem('auth_token', res.data.token);
      api.setAuthToken(res.data.token);
      onFirstLogin?.(res.data.user);
      await refreshUser();
    } catch (err) {
      setPinError(err.response?.data?.error || 'Väärä PIN');
      setPin('');
      setPinSubmitting(false);
    }
  };

  const handleDigit = (d) => {
    if (pinSubmitting) return;
    const next = pin + d;
    setPin(next);
    setPinError('');
    if (next.length === 4) {
      handlePinSubmit(selectedChild, next);
    }
  };

  const handleParentLogin = async (e) => {
    e?.preventDefault();
    if (!username || !password) return;
    setLoginLoading(true);
    setLoginError('');
    const result = await login(username, password);
    if (result.success) {
      onFirstLogin?.(result.user);
    } else {
      setLoginError(result.error);
      setLoginLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('device_onboarded', '1');
    window.location.reload();
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? 'background.paper' : '#fff',
      '& fieldset': { borderColor: isDark ? '#444' : '#ddd' },
      '&:hover fieldset': { borderColor: PINK.light },
      '&.Mui-focused fieldset': { borderColor: PINK.main },
    },
    '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: PINK.main },
    '& .MuiInputBase-input': {
      color: isDark ? '#fff' : '#1a1a1a',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${isDark ? '#2a2a3a' : '#fff'} inset`,
        WebkitTextFillColor: isDark ? '#fff' : '#1a1a1a',
        caretColor: isDark ? '#fff' : '#1a1a1a',
      },
    },
    '& .MuiFormHelperText-root': { color: isDark ? '#999' : '#888' },
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      p: 2, pb: 6,
      bgcolor: isDark ? PINK.darkBg : PINK.bg,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient gradient orbs */}
      <Box sx={{
        position: 'absolute', top: '-20%', right: '-10%',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.main}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-15%', left: '-10%',
        width: 350, height: 350, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.light}12 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%', maxWidth: 820, position: 'relative', zIndex: 1 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box component="img" src="/icons/icon-96x96.png" alt="icon-96x96.png" sx={{
              width: 64, height: 64, borderRadius: 2, mb: 2,
              boxShadow: PINK.glow,
            }} />
          </motion.div>
          <Typography variant="h4" sx={{
            fontWeight: 900,
            color: isDark ? PINK.light : PINK.dark,
            background: isDark ? PINK.gradient : undefined,
            WebkitBackgroundClip: isDark ? 'text' : undefined,
            WebkitTextFillColor: isDark ? 'transparent' : undefined,
          }}>
            Tervetuloa!
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, color: isDark ? '#aaa' : PINK.dark + 'aa' }}>
            Valitse kirjautumistapa
          </Typography>
        </Box>

        {/* Two cards */}
        <Box sx={{
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          gap: 2.5, alignItems: 'stretch',
        }}>
          {/* LEFT: Child profiles + PIN overlay */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ flex: 1, display: 'flex', position: 'relative' }}
          >
            <Box sx={{
              flex: 1, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center',
              background: isDark
                ? 'linear-gradient(140deg, rgba(233,30,140,0.10) 0%, rgba(30,30,46,0.88) 70%)'
                : 'linear-gradient(140deg, rgba(233,30,140,0.07) 0%, rgba(255,255,255,0.88) 70%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: 2,
              border: `1px solid ${isDark ? PINK.main + '33' : PINK.main + '22'}`,
              boxShadow: isDark
                ? `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.light}33`
                : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.border}`,
              minHeight: 280,
            }}>
              <Typography variant="h6" fontWeight={800} sx={{
                mb: 2, color: isDark ? PINK.light : PINK.hot,
              }}>
                Olen käyttäjä
              </Typography>

              {loadingChildren ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={28} sx={{ color: PINK.main }} />
                </Box>
              ) : children.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>Ei käyttäjiä vielä</Typography>
                </Box>
              ) : (
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
                }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(children.length, 3)}, auto)`,
                    gap: 1.5, justifyContent: 'center', justifyItems: 'center', width: '100%',
                  }}>
                    {children.map((child, i) => (
                      <motion.div
                        key={child.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.93 }}
                      >
                        <Box
                          onClick={() => handleChildClick(child)}
                          sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            gap: 0.5, cursor: 'pointer', p: 1.5, borderRadius: '16px',
                            bgcolor: getBg(child, isDark),
                            border: `2px solid ${getAccent(child)}22`,
                            transition: 'all 0.2s', minWidth: 72,
                            '&:hover': { borderColor: `${getAccent(child)}66`, boxShadow: `0 4px 16px ${getAccent(child)}22` },
                          }}
                        >
                          <UserAvatar
                            src={child.avatar_url}
                            frame={child.avatar_frame}
                            size={48}
                            bgcolor={getAccent(child)}
                            sx={{ fontWeight: 900, fontSize: '1.2rem' }}
                          >
                            {child.name?.charAt(0)}
                          </UserAvatar>
                          <Typography variant="caption" fontWeight={700} sx={{ color: getTextColor(child, isDark) }}>
                            {child.name}
                          </Typography>
                          <Chip
                            label={`Taso ${child.level || 1}`}
                            size="small"
                            sx={{
                              height: 18, fontSize: '0.6rem', fontWeight: 700,
                              bgcolor: `${getAccent(child)}22`, color: getAccent(child),
                            }}
                          />
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* PIN overlay — positioned over child card */}
            <AnimatePresence>
              {selectedChild && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.92, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92, y: 16 }}
                  transition={{
                    duration: 0.4, ease: [0.22, 1, 0.36, 1],
                    opacity: { duration: 0.12 },
                  }}
                  style={{
                    position: 'absolute', inset: -16, zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onClick={(e) => { if (e.target === e.currentTarget && !pinSubmitting) { setSelectedChild(null); setPin(''); setPinError(''); } }}
                >
                  <Box sx={{
                    p: 4, borderRadius: '32px', position: 'relative', overflow: 'hidden',
                    background: isDark
                      ? 'linear-gradient(140deg, rgba(233, 30, 140, 0.12) 0%, rgba(30,30,46,0.85) 70%)'
                      : 'linear-gradient(140deg, rgba(233, 30, 140, 0.10) 0%, rgba(255,255,255,0.85) 70%)',
                    boxShadow: isDark
                      ? `0 24px 64px rgba(0,0,0,0.8), 0 0 0 1px ${PINK.light}66`
                      : `0 24px 64px rgba(0,0,0,0.15), 0 0 0 1px ${PINK.light}66`,
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 1.5, minWidth: 300,
                  }}>
                    <motion.div
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                    >
                      <UserAvatar
                        src={selectedChild.avatar_url}
                        frame={selectedChild.avatar_frame}
                        size={56}
                        bgcolor={getAccent(selectedChild)}
                      >
                        {selectedChild.name?.charAt(0)}
                      </UserAvatar>
                    </motion.div>

                    <Typography variant="h6" fontWeight={800} sx={{ color: isDark ? '#1e1e2e' : '#1a1a1a' }}>
                      {selectedChild.name}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: pinError ? '#f44336' : (isDark ? '#bbb' : '#666'),
                      minHeight: 20,
                    }}>
                      {pinError || 'Syötä PIN-koodi'}
                    </Typography>

                    {/* PIN dots */}
                    <Box sx={{ display: 'flex', gap: 2, my: 1 }}>
                      {[0, 1, 2, 3].map(i => (
                        <PinDot key={i} filled={i < pin.length} isDark={isDark} />
                      ))}
                    </Box>

                    {/* Number pad */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2, mt: 1 }}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                        <motion.div key={d} whileTap={{ scale: 0.88 }}>
                          <Box
                            onClick={() => handleDigit(String(d))}
                            sx={{
                              width: 52, height: 52, borderRadius: '50%',
                              bgcolor: isDark ? 'background.paper' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', fontSize: '1.2rem', fontWeight: 700,
                              color: isDark ? '#fff' : '#1a1a1a',
                              border: `2px solid ${PINK.light}`,
                              transition: 'all 0.15s',
                              '&:active': { bgcolor: `${PINK.main}22` },
                              userSelect: 'none',
                            }}
                          >
                            {d}
                          </Box>
                        </motion.div>
                      ))}
                      {/* Back */}
                      <motion.div whileTap={{ scale: 0.88 }}>
                        <Box
                          onClick={() => { if (!pinSubmitting) { setSelectedChild(null); setPin(''); setPinError(''); } }}
                          sx={{
                            width: 52, height: 52, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1.1rem',
                            color: isDark ? PINK.light : PINK.main,
                            userSelect: 'none',
                            '&:active': { bgcolor: isDark ? 'background.container' : '#f5f5f5' },
                          }}
                        >
                          ←
                        </Box>
                      </motion.div>
                      {/* Zero */}
                      <motion.div whileTap={{ scale: 0.88 }}>
                        <Box
                          onClick={() => handleDigit('0')}
                          sx={{
                            width: 52, height: 52, borderRadius: '50%',
                            bgcolor: isDark ? 'background.paper' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1.2rem', fontWeight: 700,
                            color: isDark ? '#fff' : '#1a1a1a',
                            border: `2px solid ${PINK.hot}`,
                            transition: 'all 0.15s',
                            '&:active': { bgcolor: `${PINK.main}22` },
                            userSelect: 'none',
                          }}
                        >
                          0
                        </Box>
                      </motion.div>
                      {/* Backspace */}
                      <motion.div whileTap={{ scale: 0.88 }}>
                        <Box
                          onClick={() => setPin(prev => prev.slice(0, -1))}
                          sx={{
                            width: 52, height: 52, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1.1rem',
                            color: isDark ? '#999' : '#666',
                            userSelect: 'none',
                            '&:active': { bgcolor: isDark ? 'background.container' : '#f5f5f5' },
                          }}
                        >
                          ⌫
                        </Box>
                      </motion.div>
                    </Box>

                    {pinSubmitting && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <CircularProgress size={24} sx={{ mt: 1, color: PINK.main }} />
                      </motion.div>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* RIGHT: Parent login */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ flex: 1, display: 'flex' }}
          >
            <Box sx={{
              flex: 1, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center',
              background: isDark
                ? 'linear-gradient(140deg, rgba(233,30,140,0.10) 0%, rgba(30,30,46,0.88) 70%)'
                : 'linear-gradient(140deg, rgba(233,30,140,0.07) 0%, rgba(255,255,255,0.88) 70%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: 2,
              border: `1px solid ${isDark ? PINK.main + '33' : PINK.main + '22'}`,
              boxShadow: isDark
                ? `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.light}33`
                : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.border}`,
              minHeight: 280,
            }}>
              <Typography variant="h6" fontWeight={800} sx={{
                mb: 2, color: isDark ? PINK.light : PINK.hot,
              }}>
                Minulla on tunnus
              </Typography>

              <Box component="form" onSubmit={handleParentLogin} sx={{
                display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 280,
              }}>
                <TextField
                  label="Käyttäjätunnus"
                  value={username}
                  onChange={e => { setUsername(e.target.value); setLoginError(''); }}
                  size="small"
                  fullWidth
                  autoComplete="username"
                  inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
                  sx={inputSx}
                />
                <TextField
                  label="Salasana"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setLoginError(''); }}
                  size="small"
                  fullWidth
                  autoComplete="current-password"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Piilota salasana' : 'Näytä salasana'}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {loginError && (
                  <Typography variant="caption" color="error" role="alert">{loginError}</Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loginLoading || !username || !password}
                  startIcon={loginLoading ? <CircularProgress size={16} /> : <LoginIcon />}
                  sx={{
                    borderRadius: 2, textTransform: 'none', fontWeight: 700,
                    background: PINK.gradient, color: '#fff',
                    boxShadow: `0 4px 16px ${PINK.main}44`,
                    '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                    '&.Mui-disabled': { background: '#eee', color: '#aaa' },
                  }}
                >
                  Kirjaudu sisään
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Box>

        {/* Kiosk button for demo mode */}
        {demoModeEnabled && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={async () => {
                setLoginLoading(true);
                const result = await login('kioski', 'kioski');
                if (result.success) {
                  onFirstLogin?.(result.user);
                } else {
                  setLoginError('Kioski-kirjautuminen epäonnistui');
                  setLoginLoading(false);
                }
              }}
              disabled={loginLoading}
              sx={{
                background: PINK.gradient, color: '#fff',
                    boxShadow: `0 4px 16px ${PINK.main}44`,
                    '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                    '&.Mui-disabled': { background: '#eee', color: '#aaa' },
              }}
            >
              Kioski-tila (Tablet)
            </Button>
          </Box>
        )}

        {/* Skip */}
        <Box sx={{ textAlign: 'center', mt: 3, pb: 5 }}>
          <Chip
            label="Ohita"
            variant="outlined"
            onClick={handleSkip}
            sx={{
              fontSize: '0.85rem', px: 2, height: 32,
              opacity: 0.5, borderColor: PINK.main + '44', color: isDark ? PINK.light : PINK.dark,
              '&:hover': { opacity: 1, borderColor: PINK.main },
            }}
          />
        </Box>
      </motion.div>
    </Box>
  );
};

export default RoleSelectPage;
